import * as Toast from "@radix-ui/react-toast"
import { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ActionFunctionArgs, useLoaderData, useRevalidator } from "react-router-dom"
import { faCheck, faHourglass } from "@fortawesome/free-solid-svg-icons"

import { cx, fetchAPI } from "../utils"

import { Header, QueueDetail } from "../components"

import type { Queue } from "../components/QueueDetail"

interface LoaderResponse {
  queues: Queue[]
}

async function loader(): Promise<LoaderResponse> {
  const queues = await fetchAPI<Queue[]>(`/api/queues`)
  return { queues: queues.result ?? [] }
}

async function action({ request }: ActionFunctionArgs) {
  const { action, queueName, error } = Object.fromEntries(await request.formData()) as Record<
    string,
    string
  >
  let endpoint = ""
  let init: Record<string, unknown> = {}

  switch (action) {
    case "retry":
    case "delete":
      endpoint = `/api/queues/perform/${action}/on/${queueName}/ByErrorType`
      init = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: { query: error },
      }
      break
  }
  return { action, errors: await fetchAPI(endpoint, init, "raw") }
}

const Route = () => {
  const dataRevalidator = useRevalidator()
  const { queues } = useLoaderData() as LoaderResponse

  const [selectedQueue, setSelectedQueue] = useState(-1)
  const [queueRefreshRate, setQueueRefreshRate] = useState(5)
  const [showKeyboardTip, setShowKeyboardTip] = useState(false)

  useEffect(() => {
    if (localStorage.getItem("keyboardTip") !== "v01") setShowKeyboardTip(true)
  }, [])

  useEffect(() => {
    function attach(e: KeyboardEvent) {
      if (e.repeat) return

      if (e.key === "n") {
        setSelectedQueue((idx) => (idx + 1) % queues.length)
      }

      if (e.key === "p") {
        setSelectedQueue((idx) => (idx < 1 ? queues.length : idx) - 1)
      }

      if (e.key === "c") {
        setSelectedQueue(-1)
      }
    }

    window.addEventListener("keydown", attach)
    return () => window.removeEventListener("keydown", attach)
  }, [queues.length])

  useEffect(() => {
    if (dataRevalidator.state !== "idle") return

    const timerId = setInterval(dataRevalidator.revalidate, queueRefreshRate * 1e3)

    return () => clearInterval(timerId)
  }, [queueRefreshRate, dataRevalidator])

  return (
    <div className="flex h-screen w-full flex-col items-start justify-start overflow-y-auto overflow-x-hidden bg-base-300 px-5 py-5">
      <div className="w-full space-y-5">
        <Header />
        <div className="flex w-full flex-wrap items-center justify-start gap-x-5 sm:justify-center">
          <div className="flex grow items-center justify-start gap-3">
            <label htmlFor="interval" className="text-sm text-base-content sm:min-w-max">
              Auto Refresh Every
            </label>
            <select
              id="interval"
              name="timerTick"
              value={queueRefreshRate}
              onChange={(e) => setQueueRefreshRate(+e.target.value)}
              className="select select-bordered select-sm select-accent w-full max-w-[10rem] shrink bg-base-100 text-accent focus:outline-none disabled:text-base-content"
            >
              <option value="1">1 second</option>
              <option value="5">5 seconds</option>
              <option value="30">30 seconds</option>
              <option value="60">60 seconds</option>
            </select>
            <FontAwesomeIcon icon={dataRevalidator.state !== "idle" ? faHourglass : faCheck} />
          </div>
        </div>
      </div>
      <div className="divider" />
      <div
        className={cx("grid h-full min-h-0 w-full grow justify-between gap-3", [
          selectedQueue > -1,
          "grid-cols-[minmax(0,1fr)_max-content] md:grid-cols-[minmax(0,45%,minmax(0,1fr))] lg:grid-cols-[minmax(0,50%),minmax(0,1fr)] xl:grid-cols-[minmax(0,37.5%),minmax(0,1fr)]",
          "grid-cols-1",
        ])}
      >
        {selectedQueue > -1 && (
          <QueueDetail
            selected
            isMainPanel
            queue={queues[selectedQueue]}
            key={queues[selectedQueue].name}
            resetSelected={() => setSelectedQueue(-1)}
          />
        )}
        <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(200px,1fr))] place-items-center gap-[inherit] overflow-y-auto">
          {queues.map((queue, idx) => (
            <QueueDetail
              queue={queue}
              key={queue.name}
              selected={selectedQueue === idx}
              setSelected={() => setSelectedQueue(idx)}
              resetSelected={() => setSelectedQueue(-1)}
            />
          ))}
        </div>
      </div>
      <Toast.Root
        type="background"
        className="flex flex-col gap-3 rounded border-[1px] border-base-300 bg-base-100 p-3 text-base-content shadow"
        open={showKeyboardTip}
        onOpenChange={(open) => {
          if (open) return

          setShowKeyboardTip(false)
          localStorage.setItem("keyboardTip", "v01")
        }}
      >
        <Toast.Title className="text-sm font-bold" style={{ gridArea: "title" }}>
          Pro Tip: Keyboard Shortcuts
        </Toast.Title>
        <ul className="mx-3 list-inside list-disc">
          <li>n - Expand next queue</li>
          <li>p - Expand previous queue</li>
          <li>c - Collapse selected queue</li>
        </ul>
        <Toast.Close className="border-black w-max rounded border-[1px] py-1 px-3">Close</Toast.Close>
      </Toast.Root>
    </div>
  )
}

Route.loader = loader
Route.action = action

export default Route
